import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Users, Eye, Plus } from "lucide-react";
import { Link } from "wouter";
import type { Prospect } from "@shared/schema";
import { usePermissions } from "@/hooks/usePermissions";
import { useMemo } from "react";

const RECENT_PROSPECTS_KEY = "recentlyViewedProspects";

function getRecentInteractions(): Record<string, number> {
  try {
    const stored = localStorage.getItem(RECENT_PROSPECTS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

export function trackProspectView(prospectId: number) {
  try {
    const interactions = getRecentInteractions();
    interactions[prospectId.toString()] = Date.now();
    localStorage.setItem(RECENT_PROSPECTS_KEY, JSON.stringify(interactions));
  } catch {
    // Ignore storage errors
  }
}

export function RecentProspects() {
  const { can, isLoading: permissionsLoading } = usePermissions();
  const hasPermission = can("prospects:view");

  const { data: prospects = [], isLoading } = useQuery<Prospect[]>({
    queryKey: ["/api/prospects"],
    enabled: hasPermission,
  });

  const recentProspects = useMemo(() => {
    const interactions = getRecentInteractions();
    return [...(prospects ?? [])]
      .sort((a, b) => {
        const aTime = interactions[a.id.toString()] || 0;
        const bTime = interactions[b.id.toString()] || 0;
        if (aTime !== bTime) {
          return bTime - aTime;
        }
        return new Date(b.createdAt!).getTime() - new Date(a.createdAt!).getTime();
      })
      .slice(0, 5);
  }, [prospects]);

  // Don't render if user doesn't have permission
  if (!permissionsLoading && !hasPermission) {
    return null;
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "lead": return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
      case "qualified": return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case "proposal_sent": return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
      case "negotiating": return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300";
      case "closed_won": return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300";
      case "closed_lost": return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
      default: return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high": return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
      case "medium": return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
      case "low": return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
      default: return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  const formatDate = (date: string | Date | null) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString();
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-lg font-medium">Recent Prospects</CardTitle>
          <Users className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Recent Prospects</CardTitle>
        <Users className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {recentProspects.length === 0 ? (
          <div className="text-center py-6">
            <Users className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500 mb-4">No prospects yet</p>
            <Link href="/prospects">
              <Button variant="outline" size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Prospect
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {recentProspects.map((prospect) => (
              <div
                key={prospect.id}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {prospect.name}
                    </p>
                    <Badge
                      className={`text-xs ${getStatusColor(prospect.status)}`}
                    >
                      {prospect.status.replace("_", " ")}
                    </Badge>
                    <Badge
                      className={`text-xs ${getPriorityColor(
                        prospect.priority
                      )}`}
                    >
                      {prospect.priority}
                    </Badge>
                  </div>
                  <div className="flex items-center text-xs text-gray-500 mt-1 space-x-4">
                    <span>Added {formatDate(prospect.createdAt)}</span>
                    {prospect.expectedValue && (
                      <span>
                        ${parseFloat(prospect.expectedValue).toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
                <Link
                  href={
                    prospect.status === "closed_won" &&
                    (prospect as any).clientId
                      ? `/clients/${(prospect as any).clientId}`
                      : `/prospects/${prospect.id}`
                  }
                >
                  <Button variant="ghost" size="sm">
                    <Eye className="h-4 w-4" />
                  </Button>
                </Link>
              </div>
            ))}
            <div className="pt-2 border-t">
              <Link href="/prospects">
                <Button variant="ghost" size="sm" className="w-full">
                  View All Prospects ({prospects.length})
                </Button>
              </Link>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}